import { onBeforeUnmount, ref } from "vue";
import type { AxiosError } from "axios";
import AgentService from "@/services/ai/agent";
import { useToast } from "@/composables/ui/useToast";
import type {
  AgentAction,
  AgentActionDecision,
  AgentActionStatus,
  AgentBulkDecisionResult,
} from "@/dtos/Agent.dto";

// Statuses an action can still move on from by itself, after the rep's decision.
const SETTLING_STATUSES: AgentActionStatus[] = ["approved", "executing"];
// Bulk approvals send in the background; each is polled until it ends.
const POLL_MS = 3_000;
const POLL_MAX_MS = 2 * 60 * 1000;

const errorText = (err: unknown, fallback: string): string => {
  const data = (err as AxiosError<{ message?: string; msg?: string }>)?.response?.data;
  // `unwrap` throws a plain Error carrying the server's `msg` when it answers `status: false`.
  return data?.message || data?.msg || (err instanceof Error && err.message) || fallback;
};

const unwrap = <T>(result: { data: { status: boolean; msg?: string; data: T } }): T => {
  if (!result.data.status) throw new Error(result.data.msg);
  return result.data.data;
};

/**
 * Deciding on the CRM assistant's drafted actions: approve (optionally edited), reject, or
 * either for several at once. Every change to an action comes back through `onUpdate`, so the
 * caller keeps one copy of each action. Shared by the chat and the approval inbox.
 */
export function useAgentActions(onUpdate: (action: AgentAction) => void) {
  const toast = useToast();
  // Actions with a request in flight; their buttons wait.
  const busyIds = ref<Set<string>>(new Set());
  const timers = new Set<ReturnType<typeof setTimeout>>();
  onBeforeUnmount(() => timers.forEach(clearTimeout));

  const setBusy = (ids: string[], busy: boolean) => {
    const next = new Set(busyIds.value);
    ids.forEach((id) => (busy ? next.add(id) : next.delete(id)));
    busyIds.value = next;
  };

  /** Reload an action, e.g. after the server refused a decision because it already moved on. */
  const refresh = async (id: string): Promise<AgentAction | null> => {
    try {
      const action = unwrap<AgentAction>(await AgentService.getAction(id));
      onUpdate(action);
      return action;
    } catch {
      return null;
    }
  };

  const decide = async (
    action: AgentAction,
    request: () => Promise<{ data: { status: boolean; msg?: string; data: AgentAction } }>,
    fallback: string
  ): Promise<AgentAction | null> => {
    if (busyIds.value.has(action.id)) return null;
    setBusy([action.id], true);
    try {
      const updated = unwrap(await request());
      onUpdate(updated);
      return updated;
    } catch (err) {
      toast.error(errorText(err, fallback));
      // Expired, superseded or decided elsewhere: show where it really stands.
      await refresh(action.id);
      return null;
    } finally {
      setBusy([action.id], false);
    }
  };

  /** Sends at once; `payload` is the rep's edited input, in the action's own shape. */
  const approve = async (action: AgentAction, payload?: Record<string, unknown>) => {
    const updated = await decide(
      action,
      () => AgentService.approveAction(action.id, payload),
      "Couldn't approve this action. Please try again."
    );
    if (updated?.status === "failed") {
      toast.error(updated.executionResult?.error || "It couldn't be sent.");
    }
    return updated;
  };

  const reject = (action: AgentAction, reason?: string) =>
    decide(
      action,
      () => AgentService.rejectAction(action.id, reason),
      "Couldn't reject this action. Please try again."
    );

  // Poll one action until it stops sending, or give up and leave it as last seen.
  const pollUntilSettled = (id: string, deadline = Date.now() + POLL_MAX_MS) => {
    const timer = setTimeout(async () => {
      timers.delete(timer);
      const action = await refresh(id);
      if (action && SETTLING_STATUSES.includes(action.status) && Date.now() < deadline) {
        pollUntilSettled(id, deadline);
      } else {
        setBusy([id], false);
      }
    }, POLL_MS);
    timers.add(timer);
  };

  /** Approve or reject several still-pending actions in one request. */
  const decideAll = async (actions: AgentAction[], decision: AgentActionDecision) => {
    const pending = actions.filter((a) => a.status === "pending" && !busyIds.value.has(a.id));
    if (!pending.length) return;
    const ids = pending.map((a) => a.id);
    setBusy(ids, true);
    try {
      const result = unwrap<AgentBulkDecisionResult>(
        await AgentService.decideActions(ids, decision)
      );
      const decided = new Set(result.decided);
      for (const action of pending) {
        if (!decided.has(action.id)) continue;
        if (decision === "reject") {
          onUpdate({ ...action, status: "rejected" });
        } else {
          // Sending in the background now; the card shows it until the poll says how it ended.
          onUpdate({ ...action, status: "approved" });
          pollUntilSettled(action.id);
        }
      }
      if (result.skipped.length) {
        toast.error(
          `${result.skipped.length} of ${ids.length} couldn't be ${
            decision === "approve" ? "approved" : "rejected"
          }. They may have expired or already been handled.`
        );
        await Promise.all(result.skipped.map((s) => refresh(s.id)));
      }
      // Rejected ones are done; approved ones stay busy until their poll ends.
      setBusy(decision === "reject" ? ids : ids.filter((id) => !decided.has(id)), false);
    } catch (err) {
      toast.error(errorText(err, "Couldn't update these actions. Please try again."));
      setBusy(ids, false);
    }
  };

  return { busyIds, approve, reject, decideAll };
}
